/**
 * Per-slot image overrides for Featured Categories / Collections.
 * Empty / auto entries fall back to catalog-derived covers.
 */

import type { FeaturedBandImage, FeaturedBandSlot } from "./featuredBandTypes";

export type FeaturedBandImageSource = "auto" | "product" | "upload";

export type FeaturedBandImageDraft = {
  source: FeaturedBandImageSource;
  /** Shop catalog product postId when source === "product". */
  productId?: string;
  src?: string;
  zoom?: number;
  x?: number;
  y?: number;
};

/** Creator zoom slider range — clamp so corrupt cfg can't scale(50). */
export const FEATURED_BAND_ZOOM_MIN = 1;
export const FEATURED_BAND_ZOOM_MAX = 2.5;

export function clampFeaturedBandZoom(raw: unknown): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return FEATURED_BAND_ZOOM_MIN;
  return Math.min(
    FEATURED_BAND_ZOOM_MAX,
    Math.max(FEATURED_BAND_ZOOM_MIN, n),
  );
}

export function newImageDraft(
  seed?: Partial<FeaturedBandImageDraft>,
): FeaturedBandImageDraft {
  return {
    source: "auto",
    zoom: 1,
    x: 50,
    y: 50,
    ...seed,
  };
}

export function emptySlotImages(): FeaturedBandImageDraft[] {
  return [
    newImageDraft(),
    newImageDraft(),
    newImageDraft(),
    newImageDraft(),
  ];
}

function coerceDraft(raw: unknown): FeaturedBandImageDraft {
  if (!raw || typeof raw !== "object") return newImageDraft();
  const o = raw as Record<string, unknown>;
  const sourceRaw = String(o.source || "auto").toLowerCase();
  const source: FeaturedBandImageSource =
    sourceRaw === "product" || sourceRaw === "upload" ? sourceRaw : "auto";
  const x = Number(o.x);
  const y = Number(o.y);
  return {
    source,
    productId: String(o.productId || "").trim() || undefined,
    src: String(o.src || "").trim() || undefined,
    // Default 1× (Loom: not zoomed on load); creator can still raise zoom.
    zoom: clampFeaturedBandZoom(o.zoom),
    x: Number.isFinite(x) ? Math.min(100, Math.max(0, x)) : 50,
    y: Number.isFinite(y) ? Math.min(100, Math.max(0, y)) : 50,
  };
}

/** Parse cfg.slotImages → one 4-image draft array per slot. */
export function parseSlotImages(
  cfg: { slotImages?: unknown } | null | undefined,
  count: number,
): FeaturedBandImageDraft[][] {
  const raw = cfg?.slotImages;
  let parsed: unknown = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = [];
    }
  }
  const rows = Array.isArray(parsed) ? parsed : [];
  return Array.from({ length: count }, (_, i) => {
    const row = Array.isArray(rows[i]) ? rows[i] : [];
    return Array.from({ length: 4 }, (_, j) => coerceDraft(row[j]));
  });
}

export function serializeSlotImages(
  images: FeaturedBandImageDraft[][],
): string {
  return JSON.stringify(
    images.map((row) =>
      row.map((img) => {
        const source = img.source === "product" || img.source === "upload"
          ? img.source
          : "auto";
        return {
          source,
          ...(img.productId ? { productId: img.productId } : {}),
          ...(img.src ? { src: img.src } : {}),
          zoom: clampFeaturedBandZoom(img.zoom),
          x: img.x ?? 50,
          y: img.y ?? 50,
        };
      }),
    ),
  );
}

function draftHasOverride(img: FeaturedBandImageDraft): boolean {
  if (img.source === "upload") return Boolean(String(img.src || "").trim());
  if (img.source === "product") {
    return Boolean(
      String(img.src || "").trim() || String(img.productId || "").trim(),
    );
  }
  return false;
}

/** Merge catalog-built slots with creator image overrides. */
export function applySlotImageOverrides(
  slots: FeaturedBandSlot[],
  drafts: FeaturedBandImageDraft[][],
  opts?: { defaultFit?: "contain" | "cover" },
): FeaturedBandSlot[] {
  const defaultFit = opts?.defaultFit === "contain" ? "contain" : "cover";
  return slots.map((slot, i) => {
    const row = drafts[i] || emptySlotImages();
    const images: FeaturedBandImage[] = Array.from({ length: 4 }, (_, j) => {
      const base = slot.images[j] || slot.images[0] || { src: "" };
      const draft = row[j] || newImageDraft();
      if (!draftHasOverride(draft)) {
        // Auto/catalog: contain by default (not cover-crop). Honor creator zoom.
        const fit =
          base.brandSlug &&
          (base.fit === "cover" || base.fit === "contain")
            ? base.fit
            : defaultFit;
        return {
          ...base,
          zoom: clampFeaturedBandZoom(draft.zoom),
          x: draft.x ?? 50,
          y: draft.y ?? 50,
          fit,
        };
      }
      const src = String(draft.src || base.src || "").trim();
      return {
        src,
        alt: base.alt,
        label: base.label,
        brandSlug: base.brandSlug,
        zoom: clampFeaturedBandZoom(draft.zoom),
        x: draft.x ?? 50,
        y: draft.y ?? 50,
        fit:
          draft.source === "product" || defaultFit === "contain"
            ? ("contain" as const)
            : ("cover" as const),
      };
    });
    return { ...slot, images };
  });
}

/** Resolve a display URL for the configure panel (override or auto fallback). */
export function resolveDraftPreviewSrc(
  draft: FeaturedBandImageDraft,
  autoSrc: string,
): string {
  if (draft.source === "upload" || draft.source === "product") {
    return String(draft.src || "").trim() || autoSrc;
  }
  return autoSrc;
}
